import { Dispatch, FC, SetStateAction, useEffect, useState } from "react";
import Modal from "../../components/shared/Modal/Modal";
import { useTranslation } from "react-i18next";
import { ServiceNextProject } from "../../services/ServiceNextProject";
import { SelectorType } from "../../components/shared/Selector/Selector";
import NextEntityPlacementSelector from "../../components/shared/NextEntityPlacementSelector/NextEntityPlacementSelector";

type Props = {
  show: boolean,
  setShow: Dispatch<SetStateAction<boolean>>,
  projectId: string | null,
  nextProjectId?: number,
  nextCustomerId?: number,
  onSuccess: (response: any) => void,
  onError: (error: any) => void,
  allNextProjects: any[],
  fetchAllNextProjects: () => void,
  allNextCustomers: any[],
  fetchAllNextCustomers: () => void,
}

const ExportNextProjectModal: FC<Props> = ({
  show,
  setShow,
  projectId,
  nextProjectId,
  nextCustomerId,
  onSuccess,
  onError,
  allNextProjects,
  fetchAllNextProjects,
  allNextCustomers,
  fetchAllNextCustomers,
}) => {
  const [t] = useTranslation('projects');
  const [selectedParentProject, setSelectedParentProject] = useState<SelectorType>(null);
  const [selectedParentCustomer, setSelectedParentCustomer] = useState<SelectorType>(null);
  const [exporting, setExporting] = useState(false);

  const placementNeeded = !nextProjectId;
  const clientNeeded = !nextCustomerId;

  useEffect(() => {
    if (!show) return;
    if (placementNeeded && allNextProjects.length === 0) fetchAllNextProjects();
    if (clientNeeded && (allNextCustomers?.length ?? 0) === 0) fetchAllNextCustomers();
  }, [show]);

  const handleExport = async () => {
    if (!projectId) return;
    if (placementNeeded && !selectedParentProject) return;
    if (clientNeeded && !selectedParentCustomer) return;
    setExporting(true);
    try {
      const response = await ServiceNextProject.exportProject(
        projectId,
        placementNeeded && selectedParentProject ? Number(selectedParentProject.value) : undefined,
        clientNeeded && selectedParentCustomer ? Number(selectedParentCustomer.value) : undefined,
      );
      onSuccess(response);
      setShow(false);
    } catch (error) {
      onError(error);
    }
    setExporting(false);
  };

  return (
    <Modal
      title={t('exportNextProjectModal.headline', 'Skicka till Next Project')}
      show={show}
      setShow={setShow}
      buttonInfo={{
        label: t('exportNextProjectModal.button.send', 'Skicka'),
        action: handleExport,
        disabled: exporting || (placementNeeded && !selectedParentProject) || (clientNeeded && !selectedParentCustomer),
        loaderShow: exporting,
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {placementNeeded && (
          <NextEntityPlacementSelector
            value={selectedParentProject}
            onChange={setSelectedParentProject}
            options={allNextProjects.map(p => ({ label: `${p.projectno} ${p.name}`, value: String(p.id) }))}
            onRefresh={fetchAllNextProjects}
            loading={allNextProjects.length === 0}
            label={t('exportNextProjectModal.parentProjectLabel', 'Placering')}
            placeholder={t('exportNextProjectModal.selectParentProject', 'Välj överordnat projekt')}
          />
        )}
        {clientNeeded && (
          <NextEntityPlacementSelector
            value={selectedParentCustomer}
            onChange={setSelectedParentCustomer}
            options={(Array.isArray(allNextCustomers) ? allNextCustomers : []).map(c => ({ label: c.name, value: String(c.id) }))}
            onRefresh={fetchAllNextCustomers}
            loading={(allNextCustomers?.length ?? 0) === 0}
            label={t('createProject.label.customer', 'Beställare')}
            placeholder={t('createProject.placeholder.customer', 'Välj en kund från Next')}
          />
        )}
      </div>
    </Modal>
  );
};

export default ExportNextProjectModal;
